/**
 * Phase F.6 Batch D+ — Pending Acknowledgments admin page.
 * Lists every doc that requires an action and hasn't been acted on yet.
 * One-click "Nudge" sends a Notification to the recipient.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { BellRing, RefreshCw, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PendingAcknowledgments() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nudging, setNudging] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/client-documents/pending-acknowledgments`);
      setPending(data.pending || []);
    } catch (e) {
      toast.error("Failed to load — admin access required");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const nudge = async (doc) => {
    setNudging(doc.id);
    try {
      const { data } = await axios.post(`${API}/client-documents/${doc.id}/nudge`, {});
      if (data.ok) toast.success("Recipient nudged");
      else toast.warning(data.message || "Recipient has no portal user — send a manual email reminder.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nudge failed");
    } finally {
      setNudging(null);
    }
  };

  const ageBadge = (days) => {
    if (days >= 14) return <Badge variant="destructive">{days}d</Badge>;
    if (days >= 7) return <Badge variant="secondary">{days}d</Badge>;
    return <Badge variant="outline">{days}d</Badge>;
  };

  return (
    <div className="p-6 space-y-4" data-testid="pending-acks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Pending Acknowledgments</h1>
          <p className="text-muted-foreground text-sm">Documents shared with a required action that no recipient has acted on yet.</p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BellRing className="h-4 w-4 text-amber-500" />
            {pending.length} document{pending.length !== 1 ? "s" : ""} awaiting action
          </CardTitle>
          <CardDescription>Sorted oldest-first. Use Nudge to send the recipient a reminder Notification.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p> : (
            pending.length === 0 ? (
              <p className="text-sm text-muted-foreground py-10 text-center">All caught up — nobody owes you a signature right now.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Required action</TableHead>
                    <TableHead className="w-20">Age</TableHead>
                    <TableHead className="w-40 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pending.map(d => (
                    <TableRow key={d.id} data-testid={`pending-row-${d.id}`}>
                      <TableCell className="font-medium">{d.file_name}</TableCell>
                      <TableCell><Badge variant="outline">{d.category}</Badge></TableCell>
                      <TableCell><Badge>{d.requires_action}</Badge></TableCell>
                      <TableCell>{ageBadge(d.days_pending)}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="outline" disabled={nudging === d.id} onClick={() => nudge(d)} data-testid={`pending-nudge-${d.id}`}>
                          <BellRing className="h-3.5 w-3.5 mr-1" />
                          {nudging === d.id ? "Sending…" : "Nudge"}
                        </Button>
                        {d.client_id && (
                          <a href={`/admin/clients/${d.client_id}`} className="inline-flex items-center text-xs text-cyan-600 hover:underline ml-2">
                            Open client <ExternalLink className="h-3 w-3 ml-0.5" />
                          </a>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
}
